/**
 * Per-session cache for a tenant's boundary list.
 *
 * Lives outside `BoundariesOverlay` because two callers now need the same
 * data: the overlay (to draw the outlines) and `useFpaMapHome` (to work out
 * where a map should open). Callers arriving together share one in-flight
 * request rather than each firing their own `GET /boundaries`.
 */
// Legacy JS API module — typed via the permissive `@/api/*` shim, so the
// response needs a cast.
import * as boundariesApi from '@/api/boundaries';

export interface BoundaryRecord {
  id: number;
  name: string;
  /** Sanitised slug used as the parcel `boundary_name` partition key. */
  boundary_name?: string | null;
  description?: string | null;
  is_primary?: boolean;
  geometry?: { type?: string; coordinates?: unknown } | null;
}

interface BoundaryListResponse {
  boundaries?: BoundaryRecord[];
}

/** Resolved lists, keyed by tenant id. */
const cache = new Map<number, BoundaryRecord[]>();
/** Requests currently in flight, keyed by tenant id — dedupes concurrent callers. */
const inFlight = new Map<number, Promise<BoundaryRecord[]>>();

/**
 * Fetch (or return the cached) boundary list for a tenant.
 *
 * A failed fetch resolves to `[]` and is NOT cached, so a later caller can
 * retry — boundaries are decoration on most maps, so a failure warns and the
 * map carries on rather than erroring the page.
 */
export function loadTenantBoundaries(tenantId: number): Promise<BoundaryRecord[]> {
  const cached = cache.get(tenantId);
  if (cached) return Promise.resolve(cached);

  const pending = inFlight.get(tenantId);
  if (pending) return pending;

  const promise = (boundariesApi.listBoundaries({
    tenant_id: tenantId,
    page: 1,
    page_size: 200,
  }) as Promise<BoundaryListResponse>)
    .then((data) => {
      const list = data?.boundaries ?? [];
      cache.set(tenantId, list);
      return list;
    })
    .catch((err) => {
      console.warn('Failed to load boundaries:', err);
      return [] as BoundaryRecord[];
    })
    .finally(() => {
      inFlight.delete(tenantId);
    });

  inFlight.set(tenantId, promise);
  return promise;
}

/** Synchronous peek — returns the cached list, or null if not fetched yet. */
export function peekTenantBoundaries(tenantId: number): BoundaryRecord[] | null {
  return cache.get(tenantId) ?? null;
}
